import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import type React from "react";
import { useEffect, useState } from "react";
import {
  addTagToUser,
  archiveTag,
  createTag,
  getTags,
} from "../../../apis/tags/tag-apis";
import { TAG_COLORS, type Tag } from "../../../domain/tags";
import { useShowToast } from "../../../hooks";

interface AddTagModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  adminEmail: string;
  existingTagIds: string[];
  onSuccess: () => void;
}

const AddTagModal: React.FC<AddTagModalProps> = ({
  open,
  onClose,
  userId,
  adminEmail,
  existingTagIds,
  onSuccess,
}) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string>("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);

  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState<string>(TAG_COLORS[0].hex);
  const [newTagDescription, setNewTagDescription] = useState("");
  const [archiving, setArchiving] = useState(false);

  const showToast = useShowToast();

  useEffect(() => {
    if (open) {
      loadTags();
      setSelectedTagId("");
      setNote("");
      setCreateMode(false);
      setNewTagName("");
      setNewTagDescription("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const tags = await getTags(false);
      setAvailableTags(tags);
    } catch (error) {
      console.error("Error loading tags:", error);
      showToast("Failed to load tags", "error");
    } finally {
      setLoadingTags(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedTagId) return;
    setLoading(true);
    try {
      await addTagToUser(userId, selectedTagId, adminEmail, note || undefined);
      showToast("Tag added", "success");
      onSuccess();
    } catch (error: any) {
      showToast(error.response?.data?.message || "Failed to add tag", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setLoading(true);
    try {
      const newTag = await createTag(
        newTagName.trim(),
        newTagColor,
        adminEmail,
        newTagDescription || undefined
      );
      showToast("Tag created", "success");
      setAvailableTags([...availableTags, newTag]);
      setSelectedTagId(newTag.id);
      setCreateMode(false);
      setNewTagName("");
      setNewTagDescription("");
    } catch (error: any) {
      showToast(
        error.response?.data?.message || "Failed to create tag",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveTag = async () => {
    if (!selectedTagId) return;
    setArchiving(true);
    try {
      await archiveTag(selectedTagId, true);
      showToast("Tag archived", "success");
      const remaining = availableTags.filter((t) => t.id !== selectedTagId);
      setAvailableTags(remaining);
      setSelectedTagId("");
      setConfirmArchiveOpen(false);
    } catch (error: any) {
      showToast(
        error.response?.data?.message || "Failed to archive tag",
        "error"
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleOpenArchiveConfirm = () => {
    if (!selectedTagId) return;
    setConfirmArchiveOpen(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
        <DialogTitle>Add Tag</DialogTitle>
        <DialogContent>
          {loadingTags ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : createMode ? (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Tag Name"
                fullWidth={true}
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth={true} sx={{ mb: 2 }}>
                <InputLabel>Color</InputLabel>
                <Select
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  label="Color"
                >
                  {TAG_COLORS.map((color) => (
                    <MenuItem key={color.hex} value={color.hex}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "4px",
                            backgroundColor: color.hex,
                          }}
                        />
                        {color.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Description (optional)"
                fullWidth={true}
                multiline={true}
                rows={2}
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button onClick={() => setCreateMode(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Create Tag"}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <FormControl fullWidth={true} sx={{ mt: 2 }}>
                <InputLabel>Select Tag</InputLabel>
                <Select
                  value={selectedTagId}
                  onChange={(e) => setSelectedTagId(e.target.value)}
                  label="Select Tag"
                >
                  {availableTags.length === 0 ? (
                    <MenuItem disabled={true}>No tags available</MenuItem>
                  ) : (
                    availableTags.map((tag) => {
                      const isAssigned = existingTagIds.includes(tag.id);
                      return (
                        <MenuItem
                          key={tag.id}
                          value={tag.id}
                          disabled={isAssigned}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              opacity: isAssigned ? 0.5 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: tag.color,
                              }}
                            />
                            {tag.name}
                            {isAssigned && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                (already assigned)
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Note (optional)"
                placeholder="Why are you adding this tag?"
                fullWidth={true}
                multiline={true}
                rows={2}
                sx={{ mt: 2 }}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  color="error"
                  variant="outlined"
                  type="button"
                  onClick={handleOpenArchiveConfirm}
                  disabled={!selectedTagId || archiving}
                >
                  {archiving ? (
                    <CircularProgress size={18} />
                  ) : (
                    "Archive selected tag"
                  )}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button
                startIcon={<AddIcon />}
                onClick={() => setCreateMode(true)}
              >
                Create New Tag
              </Button>
            </>
          )}
        </DialogContent>
        {!createMode && (
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!selectedTagId || loading}
            >
              {loading ? <CircularProgress size={20} /> : "Add Tag"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <Dialog
        open={confirmArchiveOpen}
        onClose={() => setConfirmArchiveOpen(false)}
      >
        <DialogTitle>Archive tag?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Archiving removes the tag from selection going forward but keeps
            historical usage. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchiveOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleArchiveTag}
            disabled={archiving || !selectedTagId}
          >
            {archiving ? <CircularProgress size={18} /> : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddTagModal;
